/**
 * HighlightsModal — iter87.346 (user-requested)
 *
 * Shown from the MLB Schedule's "Highlights" button (visible once a
 * game is Final). Fetches GET /api/mlb/tournaments/{tid}/games/{gid}/highlights
 * and embeds the official MLB recap clip.
 */
import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Film } from "lucide-react";
import { api } from "../lib/api";
import { openExternalLink } from "../lib/externalLink";

export function HighlightsModal({ open, onClose, entry }) {
    const [loading, setLoading] = useState(true);
    const [clips, setClips] = useState([]);

    useEffect(() => {
        if (!open || !entry?.highlights_endpoint) return;
        setLoading(true);
        setClips([]);
        api.fetchRelative(entry.highlights_endpoint)
            .then((res) => setClips(res?.highlights || []))
            .catch(() => setClips([]))
            .finally(() => setLoading(false));
    }, [open, entry?.highlights_endpoint]);

    return (
        <Dialog open={open} onOpenChange={(v) => { if (!v) onClose?.(); }}>
            <DialogContent data-testid="highlights-modal" className="max-w-lg bg-zinc-950 border-2 border-zinc-800">
                <DialogHeader>
                    <DialogTitle className="font-display font-extrabold text-base uppercase tracking-tight flex items-center gap-2">
                        <Film className="w-4 h-4 text-cyber" /> Highlights
                    </DialogTitle>
                    <DialogDescription className="text-[11px] text-zinc-500">
                        {entry?.home_label} vs {entry?.away_label}
                    </DialogDescription>
                </DialogHeader>

                {loading && (
                    <p className="text-xs text-zinc-500" data-testid="highlights-loading">Loading highlights…</p>
                )}

                {!loading && clips.length === 0 && (
                    <p className="text-xs text-zinc-500 italic" data-testid="highlights-empty">
                        No highlight clip published for this game yet.
                    </p>
                )}

                {!loading && clips.map((c) => (
                    <div key={c.id} className="space-y-2" data-testid={`highlights-clip-${c.id}`}>
                        <div className="aspect-video w-full rounded overflow-hidden border-2 border-zinc-800">
                            <iframe
                                src={c.embed_url}
                                title={c.title}
                                className="w-full h-full"
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                allowFullScreen
                            />
                        </div>
                        <p className="text-xs text-white font-bold leading-tight">{c.title}</p>
                        <a
                            href={c.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => { e.preventDefault(); openExternalLink(c.url); }}
                            data-testid={`highlights-clip-link-${c.id}`}
                            className="text-[11px] text-cyber hover:text-volt underline decoration-dotted underline-offset-2"
                        >
                            Watch full video →
                        </a>
                    </div>
                ))}
            </DialogContent>
        </Dialog>
    );
}

export default HighlightsModal;
